import { GridPos, PlacedFurniture, CustomFloorTile, OfficeRoom } from '../types';
import { FURNITURE_CATALOG } from '../data/officeCatalog';

export const TILE_WIDTH = 64; // width of isometric tile in pixels
export const TILE_HEIGHT = 32; // height of isometric tile in pixels

/**
 * Converts 2D grid coordinates (col, row, z) to isometric screen coordinates.
 */
export function gridToIso(
  col: number,
  row: number,
  z: number = 0,
  originX: number = 0,
  originY: number = 0
): { x: number; y: number } {
  const x = originX + (col - row) * (TILE_WIDTH / 2);
  const y = originY + (col + row) * (TILE_HEIGHT / 2) - z * TILE_HEIGHT;
  return { x, y };
}

/**
 * Converts screen coordinates back to 2D grid coordinates.
 */
export function isoToGrid(
  screenX: number,
  screenY: number,
  originX: number = 0,
  originY: number = 0
): { col: number; row: number } {
  const relX = screenX - originX;
  const relY = screenY - originY;

  const col = Math.floor((relX / (TILE_WIDTH / 2) + relY / (TILE_HEIGHT / 2)) / 2);
  const row = Math.floor((relY / (TILE_HEIGHT / 2) - relX / (TILE_WIDTH / 2)) / 2);

  return { col, row };
}

/**
 * Checks if a specific grid cell is walkable (not blocked by walls or non-walkable furniture, and has floor constructed).
 */
export function isCellWalkable(
  col: number,
  row: number,
  gridWidth: number,
  gridHeight: number,
  items: PlacedFurniture[],
  customFloors?: Record<string, CustomFloorTile>
): boolean {
  if (col < 0 || col >= gridWidth || row < 0 || row >= gridHeight) {
    return false;
  }

  // If customFloors is defined and populated, check if this cell has a constructed room floor
  if (customFloors && Object.keys(customFloors).length > 0) {
    if (!customFloors[`${col},${row}`]) {
      return false; // Cell is outside constructed office rooms (void base grid)
    }
  }

  for (const item of items) {
    const def = FURNITURE_CATALOG.find((f) => f.type === item.type);
    if (!def) continue;

    if (!def.isWalkable) {
      const itemW = item.width || def.width || 1;
      const itemH = item.height || def.height || 1;

      // Check bounding box of item
      if (
        col >= item.col &&
        col < item.col + itemW &&
        row >= item.row &&
        row < item.row + itemH
      ) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Finds the shortest path using Breadth-First Search (BFS) from start to target on the isometric grid.
 */
export function findGridPath(
  start: GridPos,
  target: GridPos,
  gridWidth: number,
  gridHeight: number,
  items: PlacedFurniture[],
  customFloors?: Record<string, CustomFloorTile>
): GridPos[] {
  if (start.col === target.col && start.row === target.row) {
    return [];
  }

  // If target itself is non-walkable (e.g. coffee machine or toilet), find closest walkable adjacent cell
  let actualTarget = target;
  if (!isCellWalkable(target.col, target.row, gridWidth, gridHeight, items, customFloors)) {
    const neighbors = [
      { col: target.col + 1, row: target.row },
      { col: target.col - 1, row: target.row },
      { col: target.col, row: target.row + 1 },
      { col: target.col, row: target.row - 1 },
    ];
    const walkableAdj = neighbors.filter((n) =>
      isCellWalkable(n.col, n.row, gridWidth, gridHeight, items, customFloors)
    );
    if (walkableAdj.length > 0) {
      actualTarget = walkableAdj[0];
    } else {
      return [];
    }
  }

  const queue: { pos: GridPos; path: GridPos[] }[] = [{ pos: start, path: [start] }];
  const visited = new Set<string>();
  visited.add(`${start.col},${start.row}`);

  const directions = [
    { col: 1, row: 0 },
    { col: -1, row: 0 },
    { col: 0, row: 1 },
    { col: 0, row: -1 },
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const { pos, path } = current;

    if (pos.col === actualTarget.col && pos.row === actualTarget.row) {
      // Remove starting position from path return
      return path.slice(1);
    }

    for (const dir of directions) {
      const nextCol = pos.col + dir.col;
      const nextRow = pos.row + dir.row;
      const key = `${nextCol},${nextRow}`;

      if (
        !visited.has(key) &&
        isCellWalkable(nextCol, nextRow, gridWidth, gridHeight, items, customFloors)
      ) {
        visited.add(key);
        queue.push({
          pos: { col: nextCol, row: nextRow },
          path: [...path, { col: nextCol, row: nextRow }],
        });
      }
    }
  }

  return [];
}

/**
 * Checks if a coordinate is inside a given room boundary.
 */
export function isInsideRoom(col: number, row: number, room: OfficeRoom): boolean {
  if (room.cells && room.cells.length > 0) {
    return room.cells.some((cell) => cell.col === Math.floor(col) && cell.row === Math.floor(row));
  }
  return (
    col >= room.col &&
    col < room.col + room.width &&
    row >= room.row &&
    row < room.row + room.height
  );
}

/**
 * Gets the room that currently contains the agent.
 */
export function getAgentCurrentRoom(
  agentCol: number,
  agentRow: number,
  rooms?: OfficeRoom[]
): OfficeRoom | undefined {
  if (!rooms || rooms.length === 0) return undefined;
  return rooms.find((room) => isInsideRoom(agentCol, agentRow, room));
}

/**
 * Gets center coordinates of a room for camera centering or navigation.
 */
export function getRoomCenter(room: OfficeRoom): GridPos {
  return {
    col: Math.floor(room.col + room.width / 2),
    row: Math.floor(room.row + room.height / 2),
  };
}

/**
 * Checks whether two rectangular rooms overlap in 2D grid space.
 */
export function doRoomsOverlap(
  roomA: { col: number; row: number; width: number; height: number; id?: string },
  roomB: { col: number; row: number; width: number; height: number; id?: string }
): boolean {
  // Same room instance cannot overlap with itself
  if (roomA.id && roomB.id && roomA.id === roomB.id) {
    return false;
  }
  return (
    roomA.col < roomB.col + roomB.width &&
    roomA.col + roomA.width > roomB.col &&
    roomA.row < roomB.row + roomB.height &&
    roomA.row + roomA.height > roomB.row
  );
}

/**
 * Finds if a candidate room overlaps with any other existing room.
 * Returns the conflicting room if found, or undefined if clear.
 */
export function findOverlappingRoom(
  candidateRoom: { col: number; row: number; width: number; height: number; id?: string },
  rooms?: OfficeRoom[]
): OfficeRoom | undefined {
  if (!rooms || rooms.length === 0) return undefined;
  return rooms.find((r) => doRoomsOverlap(candidateRoom, r));
}

/**
 * Attempts to find a free, non-overlapping location on the grid for a room of given dimensions.
 */
export function findFreeRoomPosition(
  width: number,
  height: number,
  gridWidth: number,
  gridHeight: number,
  existingRooms: OfficeRoom[] = []
): { col: number; row: number } | null {
  for (let r = 1; r <= gridHeight - height; r += 2) {
    for (let c = 1; c <= gridWidth - width; c += 2) {
      const candidate = { col: c, row: r, width, height };
      const hasOverlap = existingRooms.some((er) => doRoomsOverlap(candidate, er));
      if (!hasOverlap) {
        return { col: c, row: r };
      }
    }
  }
  return null;
}

